import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Shield, Eye, ThumbsUp, Headphones } from "lucide-react";
import { useSession } from "@/App";
import { useTheme } from "@/themes/themes";

// CANONICAL ASSET - Using absolute path for routing compatibility
const HERO_ORB_URL = "/assets/orb_havn_v4.png";
const BG_IMAGE_URL = "/assets/havn_bg_dark.png";

// Background style with soft glow and vignette
const BG_STYLE = {
  backgroundImage: `
    radial-gradient(ellipse at center, transparent 0%, transparent 40%, rgba(0,0,0,0.12) 100%),
    radial-gradient(circle at center, rgba(255,250,235,0.2) 0%, rgba(255,250,235,0.08) 25%, transparent 50%),
    url(${BG_IMAGE_URL})
  `,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
};

// HAVN Tutorial Cards - Protection, Calm, Reinforcement, Support
const tutorialCards = [
  {
    id: 1,
    icon: Shield,
    title: "You're Protected",
    example: "I quietly watch for digital risks so you don't have to.",
    color: "var(--sk-primary)"
  },
  {
    id: 2,
    icon: Eye,
    title: "Clear, Calm Warnings",
    example: "If something looks unsafe, I'll explain why — without alarm.",
    color: "var(--sk-primary)"
  },
  {
    id: 3,
    icon: ThumbsUp,
    title: "Good Decisions Reinforced",
    example: "When you make a safe choice, I'll let you know.",
    color: "var(--sk-primary)"
  },
  {
    id: 4,
    icon: Headphones,
    title: "Support When Needed",
    example: "If a situation gets tricky, help is available.",
    color: "var(--sk-primary)"
  }
];

export default function TutorialPage() {
  const navigate = useNavigate();
  const { completeTutorial, sessionLogger } = useSession();
  const { theme } = useTheme();
  const [currentCard, setCurrentCard] = useState(0);

  const handleNext = () => {
    if (currentCard < tutorialCards.length - 1) {
      setCurrentCard(currentCard + 1);
    }
  };

  const handlePrev = () => {
    if (currentCard > 0) {
      setCurrentCard(currentCard - 1);
    }
  };

  const handleStart = () => {
    sessionLogger.log('tutorial_completed');
    completeTutorial();
    navigate('/home', { replace: true });
  };

  const card = tutorialCards[currentCard];
  const Icon = card.icon;
  const isLastCard = currentCard === tutorialCards.length - 1;

  return (
    <div className="havn-container" style={BG_STYLE} data-testid="tutorial-page">
      {/* No bg-gradient div needed - container has gradient */}
      
      {/* Header */}
      <header className="w-full z-10 pt-2">
        <h1 className="text-center text-[var(--sidekick-text-secondary)] text-sm font-medium tracking-wide uppercase">
          What can I ask?
        </h1>
      </header>

      <main className="flex flex-col items-center justify-center flex-1 z-10 px-6 gap-8 max-w-lg mx-auto w-full">
        {/* Hero Orb (small, emotional anchor) - themed */}
        <motion.img
          src={theme?.orbImage || HERO_ORB_URL}
          alt=""
          className="w-20 h-20 sm:w-24 sm:h-24 object-contain opacity-80 hero-orb-themed orb-state-safe"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Card */}
        <div className="relative w-full min-h-[280px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={card.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
              className="sk-glass-card p-8 text-center"
              data-testid={`tutorial-card-${card.id}`}
            >
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div 
                  className="sk-card-icon w-16 h-16 rounded-full flex items-center justify-center"
                >
                  <Icon size={32} />
                </div>
              </div>

              {/* Title */}
              <h2 className="sk-card-title text-xl sm:text-2xl mb-4">
                {card.title}
              </h2>

              {/* Example */}
              <p className="sk-card-text text-lg italic leading-relaxed">
                {card.example}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between w-full">
          <button
            onClick={handlePrev}
            disabled={currentCard === 0}
            className="p-3 rounded-full bg-[rgba(255,255,255,0.08)] text-[var(--sidekick-text-secondary)]
                       disabled:opacity-30 disabled:cursor-not-allowed
                       hover:bg-[rgba(255,255,255,0.12)] transition-colors"
            aria-label="Previous"
            data-testid="tutorial-prev"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Dots */}
          <div className="flex gap-2">
            {tutorialCards.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentCard(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  idx === currentCard 
                    ? 'bg-[var(--sk-primary)] w-6' 
                    : 'bg-[var(--sidekick-text-secondary)] opacity-40'
                }`}
                aria-label={`Go to card ${idx + 1}`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            disabled={isLastCard}
            className="p-3 rounded-full bg-[rgba(255,255,255,0.08)] text-[var(--sidekick-text-secondary)]
                       disabled:opacity-30 disabled:cursor-not-allowed
                       hover:bg-[rgba(255,255,255,0.12)] transition-colors"
            aria-label="Next"
            data-testid="tutorial-next"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Start Button (visible on last card) */}
        <AnimatePresence>
          {isLastCard && (
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              onClick={handleStart}
              className="px-10 py-4 bg-[var(--sk-primary)] text-[var(--sk-primary-foreground)] 
                         font-semibold text-lg rounded-full shadow-lg
                         hover:opacity-90 active:scale-95 transition-all duration-200"
              data-testid="start-using-button"
            >
              Start using HAVN
            </motion.button>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
